import React, { useEffect, useState } from "react";
import icons from "../../../assets/images/constantIcons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import images from "../../../assets/images/constantImages";
import { getCategoryDetails } from "../Items/CategorySlice";
import { handleError } from "../../Common/ApiCommon/ApiService";
import { updateStock } from "./stockSlice";

const AddStockForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const {
    data: categories,
    loading,
    error,
  } = useSelector((state) => state.categories);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      products: [],
    },
    validationSchema: Yup.object({
      products: Yup.array().of(
        Yup.object().shape({
          id: Yup.number().required(),
          name: Yup.string().required(),
          quantity: Yup.number().required().min(0),
          stock_available: Yup.boolean(),
        })
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          items: values.products.map((p) => ({
            id: p.id,
            stock_available: p.stock_available,
            stock_quantity: p.quantity,
          })),
        };

        await dispatch(updateStock(payload)).unwrap();
        toast.success("Stock updated successfully!", { duration: 2000 });
        resetForm();
        setSelectedCategoryId("");
      } catch (err) {
        handleError(err);
      }
    },
  });

  useEffect(() => {
    dispatch(getCategoryDetails("add_stock=true"));
  }, [dispatch]);

  const handleCategoryChange = (e) => {
    const selectedId = parseInt(e.target.value);
    setSelectedCategoryId(selectedId);

    const selectedCategory = categories?.find((cat) => cat.id === selectedId);
    if (selectedCategory?.items) {
      const mappedProducts = selectedCategory.items.map((item) => ({
        id: item.id,
        name: item.name,
        category: selectedCategory.name,
        quantity: item.stock_quantity || 0,
        stock_available: item.stock_available || false,
      }));
      formik.setFieldValue("products", mappedProducts);
    } else {
      formik.setFieldValue("products", []);
    }
  };

  const toggleStock = (index) => {
    const updated = [...formik.values.products];
    updated[index].stock_available = !updated[index].stock_available;
    formik.setFieldValue("products", updated);
  };

  const deleteProduct = (index) => {
    const filtered = formik.values.products.filter((_, i) => i !== index);
    formik.setFieldValue("products", filtered);
  };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex items-center mb-6 text-G300 cursor-pointer">
        <icons.FaArrowLeftLong
          onClick={() => navigate(-1)}
          className="text-2xl mr-2"
        />
        <span className="text-md">Back</span>
      </div>

      <h2 className="text-2xl font-semibold mb-6 text-G500">Add Stock</h2>

      <div className="mb-4 w-full max-w-sm">
        <label className="block mb-2 text-sm font-medium text-G300">
          Category
        </label>
        {loading ? (
          <p className="text-sm text-gray-500">Loading categories...</p>
        ) : error ? (
          <p className="text-sm text-red-500">Failed to load categories</p>
        ) : (
          <select
            value={selectedCategoryId}
            onChange={handleCategoryChange}
            className="w-full border border-G30 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green text-G500"
          >
            <option value="">Select a category</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="w-[754px] rounded-lg overflow-x-auto mb-6">
          <div className="min-w-[600px]">
            <div className="grid grid-cols-3 bg-green50 text-green font-semibold text-sm px-4 py-7">
              <div>PRODUCT</div>
              <div>QUANTITY</div>
              <div>STOCK AVAILABLE</div>
            </div>

            {formik.values.products.map((product, index) => (
              <div
                key={product.id}
                className="bg-white grid grid-cols-3 items-center px-4 py-7 border-t text-sm text-G300"
              >
                <input
                  type="text"
                  value={product.name}
                  readOnly
                  className="border border-G30 rounded-md px-2 py-1 w-full max-w-[200px] text-G300 bg-gray-100"
                />

                <input
                  type="number"
                  value={product.quantity === 0 ? "" : product.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    const updated = [...formik.values.products];
                    updated[index].quantity = value === "" ? 0 : parseInt(value, 10);
                    formik.setFieldValue("products", updated);
                  }}
                  placeholder="0"
                  className="border border-G30 rounded-md px-2 py-1 w-full max-w-[100px]"
                />

                <div className="flex gap-11">
                  <button type="button" onClick={() => toggleStock(index)}>
                    <img
                      src={
                        product.stock_available
                          ? images.ontogleswitch
                          : images.offtogleswitch
                      }
                      alt="Toggle Stock"
                      className="w-12 h-6 object-contain"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteProduct(index)}
                    className="text-red"
                  >
                    <icons.AiOutlineDelete className="w-6 h-6" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full max-w-md h-11 mt-8 bg-green text-white font-medium text-md py-2 px-6 rounded-lg"
        >
          Add Stock
        </button>
      </form>
    </div>
  );
};

export default AddStockForm;
